/**
 * LearningMetrics.tsx — /admin/learning-metrics. Manager/PlatformOwner-only view of WHICH learning
 * resources people click (MD-F##, PO ask #4), reskinned to the teal "upload" design system and
 * mounted as an in-place modal panel over AdminHome (LearningMetricsPanel), rather than the old
 * full-page navy screen. Two aggregate lists: most-clicked resources over the last 90 days, and
 * neglected content (MD-F37).
 *
 * READ-ONLY — this screen has no mutations, so there is no reload/revalidate anywhere: the panel
 * fetches once on open via useOverlayData (AdminHome does not preload it) and never re-fires.
 *
 * PRIVACY, made visible in the copy: every number here is an AGGREGATE that names nobody and ranks
 * no person (PC-F31/UR-F22). "Most-clicked" is cohort-suppressed server-side (a resource opened by
 * fewer than the reporting floor of distinct people never appears), so a count can never finger a
 * small identifiable group. We track CLICKS ONLY — no hours, and no search terms are ever recorded
 * (AE-F36). Nothing on this screen can be drilled down to an individual, by design.
 *
 * The still-registered /admin/learning-metrics route renders through a thin LearningMetrics() wrapper
 * that opens the same panel over a blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, type LearningMetricsPayload, type ResourceClicks } from './api';
import {
  KombiAdminOverlay,
  EmptyState,
  ErrorLine,
  Notice,
  NotPermitted,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type LearningMetricsLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; metrics: LearningMetricsPayload };

export async function learningMetricsLoader(): Promise<LearningMetricsLoaderData> {
  const res = await apiGet<LearningMetricsPayload>('/api/admin/learning-metrics');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/learning-metrics failed (${res.status})`);
  }
  return { state: 'ok', metrics: res.body };
}

/** A resource title as an external link (new tab) when we have its URL, plain teal text otherwise. */
function ResourceTitle({ title, url }: { title: string; url: string | null }) {
  if (url) {
    return (
      <a className="kombi-admin-link" href={url} target="_blank" rel="noreferrer">
        {title}
      </a>
    );
  }
  return <span>{title}</span>;
}

/**
 * The most-opened resources as a horizontal bar chart instead of a scrolling stack of cards. Each
 * row is one resource: its title, a track whose FILL is proportional to that resource's opens against
 * the busiest resource (so the leader is a full bar and everything reads relative to it at a glance),
 * and the exact figures spelled out at the end. The proportional width is the one genuinely
 * data-driven dimension, so it is the one inline style here; every colour is a Kombi token and nothing is rounded.
 *
 * Accessibility: the bar is decorative (aria-hidden) — the real numbers sit in text beside it, and
 * each row is a <li> in an ordered list, so a screen reader hears "Intro to Power Automate, 142 opens,
 * 38 distinct people" without depending on the visual bar at all.
 */
function ResourceBars({ resources }: { resources: ResourceClicks[] }) {
  // The busiest resource sets the full-width mark. Guarded so a max of 0 (impossible once suppressed,
  // but cheap to be safe) never divides — a 0/0 bar just renders empty rather than NaN%.
  const maxOpens = resources.reduce((m, r) => Math.max(m, r.opens), 0);

  return (
    <ol className="kombi-admin-rows">
      {resources.map((r, i) => {
        const pct = maxOpens > 0 ? Math.round((r.opens / maxOpens) * 100) : 0;
        return (
          <li key={r.itemRef} className="kombi-admin-bar-row">
            <div className="kombi-admin-bar-head">
              <span className="kombi-admin-bar-label">
                {/* A quiet rank marker so the ordering is legible even where two bars are close. */}
                <span className="kombi-admin-rank">{i + 1}.</span>
                <ResourceTitle title={r.title} url={r.url} />
              </span>
              <span className="kombi-admin-bar-figures">
                {r.opens} {r.opens === 1 ? 'open' : 'opens'} · {r.distinctOpeners} people
              </span>
            </div>
            <div
              aria-hidden="true"
              className="kombi-admin-bar"
              title={`${r.opens} opens by ${r.distinctOpeners} distinct people`}
            >
              <span style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The neglected list, which carries NO magnitude (just names) — so it is a headline count with the
 * names tucked inside a native <details> disclosure rather than a long scroll of one card per item.
 * A platform owner reads "7 resources unopened in 90 days" first, and opens the list only to act on it.
 * <details> is used un-JS'd for the toggle; a small chevron rotates via the open state.
 */
function NeglectedDisclosure({
  items,
}: {
  items: { itemRef: string; title: string; url: string | null }[];
}) {
  const [open, setOpen] = useState(false);
  const count = items.length;

  return (
    <details
      className="kombi-admin-disclosure"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="kombi-admin-disclosure-summary">
        <span>
          {count} {count === 1 ? 'resource' : 'resources'} unopened in 90 days
        </span>
        <span className="kombi-admin-disclosure-toggle">
          {open ? 'Hide' : 'Show'}
          <svg
            viewBox="0 0 24 24"
            width={16}
            height={16}
            aria-hidden="true"
            className={`transition-transform duration-micro ease-out ${open ? 'rotate-180' : ''}`}
          >
            <path
              d="M6 9l6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </summary>
      <ul className="kombi-admin-rows">
        {items.map((r) => (
          <li key={r.itemRef}>
            <ResourceTitle title={r.title} url={r.url} />
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → learningMetricsLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the two aggregate report sections on success. Read-only: nothing here
 * mutates, so there is no reload.
 */
export function LearningMetricsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error } = useOverlayData(learningMetricsLoader);

  return (
    <KombiAdminOverlay eyebrow="Insight" title="Learning metrics" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading learning metrics…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the metrics could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Learning metrics" />
      ) : data && data.state === 'ok' ? (
        <LearningMetricsReport metrics={data.metrics} />
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * The two aggregate sections, rendered once data has resolved ok: most-opened resources as a
 * proportional bar chart (ResourceBars) so demand reads at a glance rather than as a scroll, and
 * neglected content as a headline count with the names behind a disclosure (NeglectedDisclosure).
 */
function LearningMetricsReport({ metrics }: { metrics: LearningMetricsPayload }) {
  const { topResources, neglected } = metrics;

  return (
    <div>
      <ProvisionalNote />
      <Notice>
        What learning content people are actually opening, over the last 90 days. Every figure here
        is an aggregate — it names nobody and ranks no person. Popular resources are only shown once
        enough distinct people have opened one that the count cannot identify anyone; we record
        which links were clicked, never how long anyone spent or what they searched for.
      </Notice>

      <SectionHeader>Most-opened resources</SectionHeader>
      {topResources.length === 0 ? (
        <EmptyState>
          No resource has yet been opened by enough distinct people to report a count without
          identifying individuals.
        </EmptyState>
      ) : (
        <ResourceBars resources={topResources} />
      )}

      <SectionHeader>Neglected content</SectionHeader>
      <Notice>
        Cached learning resources that nobody has opened in the last 90 days (MD-F37) — candidates
        to retire or re-promote.
      </Notice>
      {neglected.length === 0 ? (
        <EmptyState>Every cached resource has been opened at least once in the window.</EmptyState>
      ) : (
        <NeglectedDisclosure items={neglected} />
      )}
    </div>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/learning-metrics deep link. Opens the panel
 * always-open over a blank backdrop; the panel still fetches via useOverlayData, and Close returns
 * to /admin.
 */
export function LearningMetrics() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <LearningMetricsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
